import React, { useCallback, useEffect } from 'react';

import { couponVoyageButtonClickAtom } from 'component/Incentive/Loyalty/CouponVoyage/__atoms/button_click.atom';
import Cta from 'component/Layout/Cta';
import { CtaType } from 'component/Layout/Cta/types';

import classNames from 'classnames';
import { Feature } from 'config/features';
import { useAtom } from 'jotai';

import { useFeatureList } from 'hooks/useFeature';
import { useQueryEndpoint } from 'hooks/useQueryEndpoint';
import { useTranslation } from 'hooks/useTranslation';

import { ReactComponent as IconCase } from 'media/svg/case.svg';
import { ReactComponent as IconCheckmark } from 'media/svg/checkmark.svg';
import { ReactComponent as IconExclamationMarkCircle } from 'media/svg/exclamationMarkCircle.svg';

import ErrorBoundary from 'utility/Debug/ErrorBoundary';
import { hasUrlParam } from 'utility/Provide/Url';

import { ApiEndpoint } from 'types/Request/endpoint';

import { ResponseStatus } from 'proto/Api/ResponseStatus';

import styles from './Banner.module.scss';

const removeCsCodeFromUrl = () => {
    const hasCsCode = hasUrlParam('cs_code');
    if (hasCsCode) {
        const url = new URL(window.location.href);
        url.searchParams.delete('cs_code');
        window.history.replaceState({}, '', url.toString());
    }
};

const CouponVoyageBanner = ({
    customClassList,
    onFetch,
}: {
    customClassList?: {
        Subline?: string;
        Headline?: string;
        Tagline?: string;
        CtaWrapper?: string;
        Banner?: string;
    };
    onFetch?: () => void;
}) => {
    document
        .getElementById('portal-c24-promotional-voucher-banner-skeleton')
        ?.remove();

    const { t } = useTranslation();
    const [buttonClicked, setButtonClicked] = useAtom(
        couponVoyageButtonClickAtom
    );
    const [isPromotionalVoucherPrefillActive, isPromotionalVoucherActivation] =
        useFeatureList(
            Feature.PromotionalVoucherPrefill,
            Feature.PromotionalVoucherActivation
        );

    const container = document.getElementById('js-promotional-voucher-layer');
    const promotionalData = container ? JSON.parse(container.innerHTML) : null;
    const isActivated = promotionalData?.activated || false;
    const countdown =
        promotionalData?.countdown >= 0 ? promotionalData.countdown : -1;

    const { isFetching, refetch, error, data } = useQueryEndpoint(
        ApiEndpoint.ActivateVoucher,
        {},
        {
            enabled: false,
        }
    );

    const isError = data?.status === ResponseStatus.Error || !!error;

    useEffect(() => {
        if (buttonClicked) {
            refetch().then((result) => {
                if (
                    !result.isError ||
                    result.data?.status !== ResponseStatus.Error
                ) {
                    removeCsCodeFromUrl();
                    onFetch?.();
                    setButtonClicked(false);
                }
            });
        }
    }, [buttonClicked, onFetch, refetch, setButtonClicked]);

    const onClick = useCallback(async () => {
        const result = await refetch();
        if (!result.isError || result.data?.status !== ResponseStatus.Error) {
            removeCsCodeFromUrl();
            setTimeout(() => {
                onFetch?.();
            }, 1000);
        }
    }, [onFetch, refetch]);

    const isActive = isActivated || data?.status === ResponseStatus.Success;

    const subline = isPromotionalVoucherPrefillActive
        ? `${t(
              'TRANS-your-personal-cashback-voucher-has-been-automatically-stored'
          )}`
        : `${t('TRANS-activate-your-cashback-voucher-and-save')}`;

    const promotionUntil = (() => {
        switch (true) {
            case countdown >= 2:
                return t('TRANS-only-n-days-left', { count: countdown });
            case countdown > 0:
                return t('TRANS-only-today-and-tomorrow');
            case countdown === 0:
                return t('TRANS-only-today');
            default:
                return t('TRANS-promotion-extended-until-date', {
                    date: promotionalData?.promotionUntil,
                });
        }
    })();

    if (!promotionalData) {
        return null;
    }

    return (
        <ErrorBoundary>
            <div className={classNames(styles.Banner, customClassList?.Banner)}>
                <div className={styles.Image}>
                    <IconCase />
                </div>
                <div className={styles.TextContent}>
                    <p
                        className={classNames(
                            styles.Tagline,
                            customClassList?.Tagline,
                            {
                                [styles.TaglineWithControlFeature]:
                                    isPromotionalVoucherActivation,
                            }
                        )}
                    >
                        {promotionUntil}
                    </p>
                    <h1
                        className={classNames(
                            styles.Headline,
                            customClassList?.Headline
                        )}
                    >
                        {t('TRANS-secure-up-to-500-euro-cashback')}
                    </h1>
                    <p
                        className={classNames(
                            styles.Subline,
                            customClassList?.Subline,
                            {
                                [styles.SublineWithControlFeature]:
                                    isPromotionalVoucherActivation,
                            }
                        )}
                    >
                        <span>{subline}</span>
                        {!isPromotionalVoucherActivation && (
                            <span className={styles.Line}>
                                {t('TRANS-while-supplies-last')}.
                            </span>
                        )}
                    </p>
                </div>
                {isPromotionalVoucherActivation && (
                    <div
                        className={classNames(
                            customClassList?.CtaWrapper,
                            styles.CtaWrapper
                        )}
                    >
                        <Cta
                            type={CtaType.BUTTON}
                            withLoader={isFetching}
                            onClick={onClick}
                            customClassList={{
                                Cta: isActive ? styles.ActiveCta : styles.Cta,
                                Spinner: isFetching ? styles.Loader : '',
                            }}
                        >
                            <span>
                                {isActive
                                    ? t('TRANS-cashback-activated')
                                    : t('TRANS-activate-cashback')}
                                {isActive && !isFetching && <IconCheckmark />}
                            </span>
                        </Cta>
                        {isError && (
                            <div className={styles.ErrorMessage}>
                                <IconExclamationMarkCircle />
                                <span>
                                    {t(
                                        'TRANS-an-error-occurred-please-try-again'
                                    )}
                                </span>
                            </div>
                        )}
                    </div>
                )}
                {isPromotionalVoucherPrefillActive && (
                    <button className={styles.ActivatedButton}>
                        {t('TRANS-cashback-activated')}
                        <div className={styles.IconWrapper}>
                            <IconCheckmark />
                        </div>
                    </button>
                )}
            </div>
        </ErrorBoundary>
    );
};

export default CouponVoyageBanner;
